/**
 * The one piece of Phase 7c that talks to Amplify. Kept in its own file, and as thin as it can be, because
 * it is the part a unit test cannot reach — everything with a decision in it lives in `change-socket.ts`
 * behind the injected `ChannelConnector`.
 *
 * ⚠️ `events` is marked `@experimental` by Amplify ("API may change in future versions"). It is the only
 * client for AppSync Events, so there is no alternative, but a minor `aws-amplify` bump can move it: keep
 * the surface used here to `connect` + `subscribe` + `close` so a break is small and obvious.
 */
import { Amplify } from 'aws-amplify';
import { events } from 'aws-amplify/data';
import type { ChangePushTarget, ChannelLike } from './change-socket';

let configuredFor: string | undefined;

/**
 * Register the Events endpoint with Amplify, once per distinct endpoint.
 *
 * ⚠️ **`Amplify.configure` REPLACES the whole config, it does not merge.** The app configures `Auth` at
 * startup (`amplify.ts`), so calling it with only `API` would silently deauthenticate the entire dashboard
 * — sign-in, token refresh, every REST call. Spreading `getConfig()` is mandatory, not defensive.
 *
 * Cannot be folded into `configureAmplify()`: that runs before sign-in from build-time env, whereas the
 * endpoint arrives at runtime from `GET /changes` (see the contract's `push` docs for why the server owns
 * it).
 */
function ensureConfigured(target: ChangePushTarget): void {
  if (configuredFor === target.endpoint) return;
  const existing = Amplify.getConfig();
  Amplify.configure({
    ...existing,
    API: {
      ...existing.API,
      Events: {
        endpoint: target.endpoint,
        region: target.region,
        // The per-call `authMode: 'none'` + `authToken` override is what actually authenticates; this only
        // sets the fallback. 'none' rather than 'userPool' so a missed override can never quietly start
        // sending an ACCESS token — it would fail loudly with "No auth token specified" instead.
        defaultAuthMode: 'none',
      },
    },
  });
  configuredFor = target.endpoint;
}

/** Test seam — the module-level "already configured" memo has to be resettable. */
export function resetChangeSocketAmplifyConfig(): void {
  configuredFor = undefined;
}

/**
 * Open the channel the SERVER named. The channel string is never constructed here: it is echoed from
 * `push.channel`, i.e. the publisher's own constant, because Amplify forwards it to AppSync verbatim and a
 * mismatch would subscribe to a channel nothing publishes to — failing as silence rather than an error.
 */
export const connectChangeChannel = async ({
  target,
  idToken,
}: {
  target: ChangePushTarget;
  idToken: string;
}): Promise<ChannelLike> => {
  ensureConfigured(target);
  const channel = await events.connect(target.channel, {
    authMode: 'none',
    authToken: idToken,
  });
  return channel as unknown as ChannelLike;
};
